import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Megaphone, Plus, MessageCircle, Share2, ChevronRight, Image as ImageIcon, Video, FileText, ArrowLeft,
} from "lucide-react";
import { api } from "../../lib/api";
import { ActiveCompanyBadge } from "../../components/ActiveCompanyBadge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import CampaignWizard from "../../components/campaign/CampaignWizard";
import PublishPanel from "../../components/campaign/PublishPanel";
import { ChannelSelect, Checklist } from "../../components/campaign/CampaignSetup";

export default function GuidedCampaigns() {
  const [params, setParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState(null);
  const [funnelLink, setFunnelLink] = useState("");
  const [detail, setDetail] = useState(null);

  const nuevo = params.get("nuevo");
  const canal = params.get("canal");
  const paso = params.get("paso");

  const load = () => api.get("/guided/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));

  useEffect(() => {
    load();
    api.get("/funnels").then((r) => {
      if (r.data?.length) setFunnelLink(`${window.location.origin}/f/${r.data[0].slug}`);
    }).catch(() => {});
  }, []);

  const openDetail = async (gc) => {
    const { data } = await api.get(`/guided/campaigns/${gc.gc_id}`);
    setDetail(data);
  };

  const Header = ({ children }) => (
    <div data-testid="guided-campaigns-page">
      <button onClick={() => setParams({})} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-4">
        <ArrowLeft size={16} /> Volver a mis campañas
      </button>
      <h1 className="font-display font-black tracking-tight text-3xl mb-6">Nueva Campaña</h1>
      {children}
    </div>
  );

  // STEP 3+: el wizard (contenido -> publicar). Empieza ya con el canal elegido.
  if (nuevo && paso === "crear" && canal) {
    return (
      <Header>
        <CampaignWizard
          funnelLink={funnelLink}
          initialType={canal}
          onCancel={() => setParams({ nuevo: "1", canal })}
          onDone={() => { setParams({}); load(); }}
        />
      </Header>
    );
  }

  // STEP 2: checklist de requisitos según el canal
  if (nuevo && canal) {
    return (
      <Header>
        <Checklist
          canal={canal}
          onBack={() => setParams({ nuevo: "1" })}
          onContinue={() => setParams({ nuevo: "1", canal, paso: "crear" })}
        />
      </Header>
    );
  }

  // STEP 1: elegir canal
  if (nuevo) {
    return (
      <Header>
        <ChannelSelect onSelect={(c) => setParams({ nuevo: "1", canal: c })} onCancel={() => setParams({})} />
      </Header>
    );
  }

  // Lista de campañas
  return (
    <div data-testid="guided-campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <div className="flex items-center gap-3">
          <Megaphone className="text-purple-500" size={26} />
          <h1 className="font-display font-black tracking-tight text-3xl">Campañas</h1>
        </div>
        <Button data-testid="new-campaign-button" onClick={() => setParams({ nuevo: "1" })} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-6 py-5">
          <Plus size={18} className="mr-2" /> Nueva Campaña
        </Button>
      </div>
      <p className="text-zinc-400 mb-3">Tu línea de tiempo: elige el canal, cumple los requisitos, genera contenido y publica.</p>
      <div className="mb-8"><ActiveCompanyBadge /></div>

      {campaigns === null && <p className="text-zinc-500">Cargando...</p>}
      {campaigns?.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-10 text-center">
          <Megaphone className="mx-auto text-zinc-600 mb-3" size={32} />
          <p className="text-zinc-400 mb-4">Aún no tienes campañas. Crea la primera en minutos.</p>
          <Button data-testid="empty-new-campaign" onClick={() => setParams({ nuevo: "1" })} className="bg-purple-600 hover:bg-purple-500 rounded-full px-6">
            <Plus size={18} className="mr-2" /> Crear mi primera campaña
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {campaigns?.map((c) => (
          <button key={c.gc_id} data-testid="campaign-item" onClick={() => openDetail(c)} className="text-left rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-purple-500/60 hover:-translate-y-1 transition-all">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold ${c.type === "whatsapp" ? "bg-green-500/15 text-green-400" : "bg-purple-500/15 text-purple-300"}`}>
                {c.type === "whatsapp" ? <MessageCircle size={13} /> : <Share2 size={13} />}
                {c.type === "whatsapp" ? "WhatsApp" : "Redes Sociales"}
              </span>
              <ChevronRight size={18} className="text-zinc-500" />
            </div>
            <h3 className="font-display font-bold text-lg mt-3">{c.title}</h3>
            <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><FileText size={13} /> {c.text ? 1 : 0}</span>
              <span className="flex items-center gap-1"><ImageIcon size={13} /> {c.images?.length || 0}</span>
              <span className="flex items-center gap-1"><Video size={13} /> {c.videos?.length || 0}</span>
              {c.publish_method && <span className="ml-auto text-purple-400">{c.publish_method === "auto" ? "Auto" : "Manual"}</span>}
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {detail?.type === "whatsapp" ? <MessageCircle className="text-green-400" size={22} /> : <Share2 className="text-purple-400" size={22} />}
              {detail?.title}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <PublishPanel campaign={detail} funnelLink={funnelLink} onMethodChange={() => { openDetail(detail); load(); }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
